import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { getActiveTerms, updateTerms } from '../api/terms';
import './TermsEdit.css';

const TermsEdit = () => {
  const [termsContent, setTermsContent] = useState('');
  const [couponTermsContent, setCouponTermsContent] = useState('');
  const [originalTermsContent, setOriginalTermsContent] = useState('');
  const [originalCouponTermsContent, setOriginalCouponTermsContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadTermsContent();
  }, []);

  const loadTermsContent = async () => {
    setIsLoading(true);
    try {
      const response = await getActiveTerms();
      if (response.success) {
        const { general, coupon } = response.data;
        
        setTermsContent(general);
        setCouponTermsContent(coupon);
        
        setOriginalTermsContent(general);
        setOriginalCouponTermsContent(coupon);
      } else {
        throw new Error(response.message || '利用規約の読み込みに失敗しました');
      }
    } catch (error) {
      console.error('利用規約の読み込みに失敗しました:', error);
      setAlert({
        open: true,
        message: error.message || '利用規約の読み込みに失敗しました',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (termsContent.trim() === '' || couponTermsContent.trim() === '') {
      setAlert({
        open: true,
        message: '利用規約とクーポン利用規約の両方の内容を入力してください',
        severity: 'warning'
      });
      return;
    }

    setIsSaving(true);
    try {
      const termsData = {
        general_content: termsContent,
        coupon_content: couponTermsContent,
      };
      
      const response = await updateTerms(termsData);

      if (response.success) {
        setOriginalTermsContent(termsContent);
        setOriginalCouponTermsContent(couponTermsContent);
        
        setAlert({
          open: true,
          message: response.message || '利用規約とクーポン利用規約を保存しました',
          severity: 'success'
        });
      } else {
        throw new Error(response.message || '保存に失敗しました');
      }
    } catch (error) {
      console.error('保存に失敗しました:', error);
      setAlert({
        open: true,
        message: error.message || '保存に失敗しました',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const hasChanges = () => {
    return termsContent !== originalTermsContent || 
           couponTermsContent !== originalCouponTermsContent;
  };

  if (isLoading) {
    return (
      <Container className="terms-edit-container">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container className="terms-edit-container" maxWidth="lg">
      <Paper className="terms-edit-paper" elevation={3}>
        <Box className="terms-edit-header">
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ backgroundColor: 'transparent' }}
          >
            規約編集
          </Typography>
        </Box>

        {hasChanges() && (
          <Alert severity="info" sx={{ mb: 2 }}>
            未保存の変更があります
          </Alert>
        )}

        <Box className="terms-editor">
          <Box className="terms-section">
            <Typography variant="h6" gutterBottom>
              利用規約内容
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={5}
              value={termsContent}
              onChange={(e) => setTermsContent(e.target.value)}
              placeholder="利用規約の内容を入力してください..."
              variant="outlined"
              className="terms-textfield"
            />
          </Box>

          <Box className="coupon-terms-section">
            <Typography variant="h6" gutterBottom>
              クーポン利用規約内容
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={5}
              value={couponTermsContent}
              onChange={(e) => setCouponTermsContent(e.target.value)}
              placeholder="クーポン利用規約の内容を入力してください..."
              variant="outlined"
              className="terms-textfield coupon-terms"
            />
          </Box>
        </Box>

        <Box className="save-button-container">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            className="save-button green"
            size="large"
            sx={{
              backgroundColor: '#4caf50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
              },
              minWidth: '200px',
              padding: '12px 24px',
            }}
          >
            {isSaving ? '登録中...' : '登録'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TermsEdit;
